import { useAuth } from "@clerk/react";
import { PanelLeft } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { cn } from "../../lib/utils";

import { useEntitlements } from "../../hooks/useEntitlements";
import {
  fetchActiveAgentConversation,
  fetchConversationList,
  resumeConversation,
  seedFromHistory,
  type AgentConversationSummary,
} from "../../lib/advisor-history";
import type { AdvisorMessage } from "../../lib/advisor-types";
import { useAdvisorStore } from "../../stores/advisor";
import type { DashboardSnapshot } from "../../lib/dashboard";
import { triggerPaywall } from "../billing/http-paywall-interceptor";
import { useAdvisorSession } from "./AdvisorSessionProvider";
import { AdvisorChatSurface } from "./advisor-chat-surface";
import { AdvisorConversationSidebar } from "./advisor-conversation-sidebar";
import { AdvisorWidgetDock } from "./advisor-widget-dock";

// SILL-247 sidebar persistence key. Stored locally so refreshing the page
// doesn't churn the user's preferred layout. Two-state ("open"/"closed")
// is enough — anything richer (per-screen-size memory) belongs in a
// follow-up if we find the simpler model wrong.
const SIDEBAR_OPEN_STORAGE_KEY = "sill.advisor.sidebar.open.v3";

// Agent type this surface lists / creates conversations for. Mirrors the
// default in `fetchConversationList` — keep them in sync if this surface ever
// hosts a different agent.
const ADVISOR_AGENT_TYPE = "readiness-advisor";

// Synthetic id for the optimistic "pending" conversation row (see the pending
// state below). Namespaced so it can never collide with a real UUID row id.
const PENDING_CONVERSATION_ID = "__pending_new_conversation__";

function readPersistedSidebarOpen(): boolean {
  if (typeof window === "undefined") return true;
  try {
    const stored = window.localStorage.getItem(SIDEBAR_OPEN_STORAGE_KEY);
    if (stored === "false") return false;
    if (stored === "true") return true;
    return true;
  } catch {
    return true;
  }
}

function persistSidebarOpen(open: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      SIDEBAR_OPEN_STORAGE_KEY,
      open ? "true" : "false",
    );
  } catch {
    // SSR / privacy-mode browsers — ignore.
  }
}

const DEFAULT_SUGGESTIONS = [
  "What is the fastest way to improve my readiness score?",
  "Which milestone should I tackle next?",
  "How sensitive is my payment to rate changes?",
  "What should I do before talking to a lender?",
] as const;

// Workspace empty-state prompts are tighter and result-oriented — they
// land widgets directly in the dock when clicked. The chat-pane empty
// state already lists more conversational suggestions above, so these
// stay short on purpose.
const DEFAULT_DOCK_EXAMPLE_PROMPTS = [
  "Estimate my monthly payment",
  "Compare down payment options",
  "How much home can I afford?",
] as const;

export function AdvisorLiveChat({
  contextRail,
  currentSnapshotId,
  initialPromptAction = "draft",
  initialPrompt,
  onProfileSnapshotUpdated,
  snapshot,
  suggestions = DEFAULT_SUGGESTIONS,
}: {
  contextRail?: ReactNode;
  // currentSnapshotId is the user's active profile snapshot ID at render
  // time. Threaded through to the dock + chat surface so widgets that were
  // computed against a now-superseded snapshot can render the "based on
  // previous inputs" badge.
  currentSnapshotId?: string | null;
  initialPromptAction?: "draft" | "submit_new";
  initialPrompt?: string;
  onProfileSnapshotUpdated?: () => void;
  snapshot?: DashboardSnapshot;
  suggestions?: readonly string[];
}) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  // Computed early (before the session hook below) because the session's
  // one-shot hydration needs to know whether THIS mount is about to blow
  // away history with a fresh `new_conversation` — see `suppressHydration`.
  const initialPromptText = initialPrompt?.trim() ?? "";
  const shouldSubmitInitialPrompt =
    initialPromptAction === "submit_new" && initialPromptText.length > 0;
  const initialDraft = shouldSubmitInitialPrompt ? undefined : initialPrompt;

  // Session (plan 435 M6, task asd-session-provider): reads the ancestor
  // `AdvisorSessionProvider`'s shared session when mounted under one, or
  // self-provides an equivalent eager-opened session scoped to this
  // component instance at this surface's one standalone call site. Lifted
  // out of this component: the WebSocket connection, the one-shot history
  // hydration, and the `new_conversation`-then-clear "New conversation"
  // protocol (exposed as `startNewConversation`, used by the M6 drawer —
  // this component keeps its own pre-existing `handleNewChat`/
  // `handleMobileNewChat`, which also reconcile the optimistic sidebar row,
  // unchanged below).
  const session = useAdvisorSession({
    suppressHydration: shouldSubmitInitialPrompt,
  });
  const {
    conversationId,
    hydrated,
    isStreaming,
    messages,
    requestFreshStart,
    send,
    startNewConversation,
    token,
  } = session;
  const dockItems = useAdvisorStore((state) => state.dockItems);
  const lastError = useAdvisorStore((state) => state.lastError);
  const addMessage = useAdvisorStore((state) => state.addMessage);
  const setStreaming = useAdvisorStore((state) => state.setStreaming);
  const stopActiveTurn = useAdvisorStore((state) => state.stopActiveTurn);
  const clearMessages = useAdvisorStore((state) => state.clearMessages);
  const setConversationId = useAdvisorStore((state) => state.setConversationId);
  const loadHistory = useAdvisorStore((state) => state.loadHistory);
  const profileSnapshotRefreshNonce = useAdvisorStore(
    (state) => state.profileSnapshotRefreshNonce,
  );

  // Sill Plus paywall trigger for the daily chat quota. The backend emits a
  // generic `error` frame with `code: "rate_limited"` when the per-user chat
  // rate limit fires; free users get routed to the global paywall
  // (GlobalUpgradeSheet) via triggerPaywall("chat_quota") so the focused
  // UpgradeModal shows first. Plus users fall through to the existing
  // lastError handling — they hit the operational 500/day ceiling, not the
  // free-tier cap. The Zustand store + WS hook stay generic — branching
  // lives in the UI layer.
  //
  // The fired-ref tracks the last error reference we already routed to
  // the paywall so we don't re-fire on every render while the error
  // stays the most-recent one. A ref (not state) avoids the
  // setState-in-effect cascade the lint rule forbids; the next
  // rate_limited frame produces a new error object, the ref diverges,
  // and the paywall reopens as expected.
  const { isPlus } = useEntitlements({ getToken });
  const lastFiredPaywallErrorRef = useRef<typeof lastError>(null);
  useEffect(() => {
    if (
      !isPlus &&
      lastError?.code === "rate_limited" &&
      lastError !== lastFiredPaywallErrorRef.current
    ) {
      lastFiredPaywallErrorRef.current = lastError;
      triggerPaywall("chat_quota");
    }
  }, [lastError, isPlus]);
  // Fast-path for staging-incident-2: the WS info frame announces a new
  // snapshot ID before /api/users/me round-trips and updates the prop
  // currentSnapshotId. The dock treats both as "known current" so older
  // widgets badge stale immediately after the agent updates a profile
  // field, rather than waiting on the bootstrap refetch.
  const latestProfileSnapshotId = useAdvisorStore(
    (state) => state.latestProfileSnapshotId,
  );
  const latestProfileRecencyToken = useAdvisorStore(
    (state) => state.latestProfileRecencyToken,
  );

  // SILL-247 sidebar state. Persisted to localStorage so a refresh keeps
  // the user's preferred layout — see SIDEBAR_OPEN_STORAGE_KEY.
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(() =>
    readPersistedSidebarOpen(),
  );
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isWorkspaceCollapsed, setIsWorkspaceCollapsed] = useState(false);
  // `null` is the initial-load sentinel — distinguishes "haven't fetched
  // yet" (skeleton) from "fetched and got an empty list" (empty-state).
  // Replacing useState's loading boolean avoids the React-19 warning
  // about setState() inside an effect.
  const [conversations, setConversations] = useState<
    AgentConversationSummary[] | null
  >(null);
  // Bumped after every action that should produce a fresh conversation
  // list (resume, new chat). End-of-stream refreshes ride on `isStreaming`
  // being a fetch-effect dependency below — no second effect needed.
  const [conversationsRefreshKey, setConversationsRefreshKey] = useState(0);
  // Optimistic "pending" sidebar row. The server creates a new conversation
  // lazily on the first message and never surfaces its id mid-stream, so a
  // just-started chat would otherwise have no row (and no selection) in the
  // sidebar until the stream ends and the list refetches. We synthesize a
  // selected row the instant the user sends the first message, then reconcile
  // it against the real list on the post-stream refetch (the ref lets the
  // fetch effect see the pending state without taking it as a dependency).
  const [pendingConversation, setPendingConversation] =
    useState<AgentConversationSummary | null>(null);
  const pendingConversationRef = useRef(false);
  const submittedInitialPromptRef = useRef<string | null>(null);
  const isHydrating =
    isLoaded && !!isSignedIn && !hydrated && !shouldSubmitInitialPrompt;

  useEffect(() => {
    if (profileSnapshotRefreshNonce === 0) return;
    onProfileSnapshotUpdated?.();
  }, [onProfileSnapshotUpdated, profileSnapshotRefreshNonce]);

  // Pull the sidebar list whenever the desktop history pane is mounted,
  // the user resumes / starts new, or a stream ends. Fetching while a
  // stream is in flight would also miss the just-derived title, so the
  // effect skips that window. `isStreaming` flipping false re-runs the
  // effect naturally — no separate watcher needed.
  useEffect(() => {
    if (!token || isStreaming) return;
    const controller = new AbortController();
    // Capture the pending flag at fetch START, not at resolve. Only a fetch
    // kicked off after the optimistic row was inserted (i.e. the post-stream
    // refetch, once isStreaming flips back to false) should reconcile it — a
    // list fetch already in flight when the user sent could otherwise resolve
    // late and adopt the wrong "newest" row.
    const reconcilePending = pendingConversationRef.current;
    fetchConversationList(token, { signal: controller.signal })
      .then((res) => {
        setConversations(res.conversations);
        // Reconcile the optimistic pending row: the turn that just ended
        // created (or wrote to) a conversation, so the newest row is the one
        // we optimistically inserted. Adopt it as the active conversation so
        // it stays selected, then drop the synthetic placeholder.
        if (reconcilePending) {
          pendingConversationRef.current = false;
          setPendingConversation(null);
          const newest = res.conversations[0];
          if (newest) setConversationId(newest.id);
        }
      })
      .catch((err: unknown) => {
        // An aborted fetch is intentional teardown (a dependency changed or
        // the component unmounted), NOT a failure — the next effect run
        // refetches. Treating it as an error here blanked the list to `[]`,
        // flickering "No chats yet" every time the user sent a message
        // (isStreaming flips true → cleanup aborts the in-flight refetch).
        if ((err as { name?: string })?.name === "AbortError") return;
        // Genuine failure: keep whatever list we already have rather than
        // wiping it. Only seed the empty-state on the very first load.
        setConversations((prev) => prev ?? []);
      });
    return () => {
      controller.abort();
    };
  }, [token, isStreaming, conversationsRefreshKey, setConversationId]);

  const handleSubmit = useCallback(
    (text: string) => {
      if (isStreaming) return;
      // A submit against an empty pane opens a fresh conversation (brand-new
      // user, or right after "New chat"). Read the store directly so this
      // doesn't need `messages` as a dependency and can't go stale.
      const startsNewConversation =
        useAdvisorStore.getState().messages.length === 0;
      const nowISO = new Date().toISOString();
      const userMessage: AdvisorMessage = {
        id: `user-${Date.now()}`,
        role: "user",
        text,
        createdAt: nowISO,
      };
      addMessage(userMessage);
      setStreaming(true);
      if (startsNewConversation) {
        // Optimistically show the new chat in the sidebar, selected, titled
        // with the message text (the server derives the same title from the
        // first user message). Reconciled on the post-stream refetch above.
        pendingConversationRef.current = true;
        setPendingConversation({
          id: PENDING_CONVERSATION_ID,
          agent_type: ADVISOR_AGENT_TYPE,
          status: "active",
          title: text,
          message_count: 1,
          last_message_at: nowISO,
          created_at: nowISO,
          updated_at: nowISO,
        });
      }
      send({ type: "message", text });
    },
    [addMessage, isStreaming, send, setStreaming],
  );

  const handleStopStreaming = useCallback(() => {
    if (!isStreaming) return;
    stopActiveTurn();
    send({ type: "cancel" });
    setConversationsRefreshKey((k) => k + 1);
  }, [isStreaming, send, stopActiveTurn]);

  useEffect(() => {
    if (!token || !shouldSubmitInitialPrompt) return;
    if (submittedInitialPromptRef.current === initialPromptText) return;

    submittedInitialPromptRef.current = initialPromptText;
    // Plan 435 M6 integration pass: under a SHARED `AdvisorSessionProvider`
    // (every real mount, once `ConsumerFrame` wraps the route tree) this
    // mount's `suppressHydration` intent is silently ignored — the ancestor
    // session hydrates independently of any one shell's plans. Flagging the
    // fresh start here guarantees the hydration fetch's eventual `.then`
    // (which always resolves strictly after this synchronous effect, same
    // commit) skips overwriting the conversation this submit is about to
    // start. Harmless no-op when self-providing (suppressHydration already
    // skips the fetch there).
    requestFreshStart();
    clearMessages();
    send({ type: "new_conversation" });
    handleSubmit(initialPromptText);
    // `hydrated` flips true as soon as the session's hydration effect sees
    // this mount suppress the fetch (see `useAdvisorSession`'s
    // `suppressHydration` option above) — no manual flip needed here.
    setConversationsRefreshKey((k) => k + 1);
  }, [
    clearMessages,
    initialPromptText,
    handleSubmit,
    requestFreshStart,
    send,
    shouldSubmitInitialPrompt,
    token,
  ]);

  // SILL-247: New chat flow. Routes through the SAME
  // `session.startNewConversation()` protocol the drawer's header "New
  // conversation" uses (review fix, iteration 2, finding 2): sends
  // `{type: "new_conversation"}`, clears the shared message store, AND
  // clears the drawer's `activeContext` (a fresh conversation must not
  // inherit a prior turn's entity context — see `AdvisorSessionProvider`).
  // The server replies with `info: conversation_reset`, which the WS
  // handler ignores because the store is already clear.
  const handleNewChat = useCallback(() => {
    if (isStreaming) return; // Defense in depth — header button is also disabled.
    startNewConversation();
    // Drop any lingering optimistic row (normally already reconciled) so the
    // freshly cleared pane doesn't keep a stale "pending" entry selected.
    pendingConversationRef.current = false;
    setPendingConversation(null);
    setConversationsRefreshKey((k) => k + 1);
  }, [isStreaming, startNewConversation]);

  const handleMobileNewChat = useCallback(() => {
    handleNewChat();
    if (!isStreaming) setIsMobileSidebarOpen(false);
  }, [handleNewChat, isStreaming]);

  // SILL-247: Resume flow. POSTs to /resume → server marks the chosen
  // conversation active + invalidates the session cache → we re-fetch
  // /active to seed the chat surface with the resumed transcript.
  // Disable-while-streaming handled at the sidebar layer; this is a no-op
  // if it slips through.
  const handleResume = useCallback(
    async (id: string) => {
      if (!token || isStreaming) return;
      try {
        await resumeConversation(token, id);
        const fresh = await fetchActiveAgentConversation(token);
        loadHistory(seedFromHistory(fresh));
        setConversationsRefreshKey((k) => k + 1);
      } catch {
        // If resume fails (e.g. someone else already closed the row,
        // network blip), fall back to listing again so the sidebar
        // reflects truth — leave the current chat surface untouched.
        setConversationsRefreshKey((k) => k + 1);
      }
    },
    [token, isStreaming, loadHistory],
  );

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarOpen((prev) => {
      const next = !prev;
      persistSidebarOpen(next);
      return next;
    });
  }, []);

  const handleToggleMobileSidebar = useCallback(() => {
    setIsMobileSidebarOpen((prev) => !prev);
  }, []);

  const handleToggleWorkspace = useCallback(() => {
    setIsWorkspaceCollapsed((prev) => !prev);
  }, []);

  // Always render the dock when a contextRail exists so the empty-state
  // example prompts surface on first load. Previously the dock was only
  // mounted once items existed, which left the right pane silent until
  // the first calculator returned.
  const dock = contextRail ? (
    <AdvisorWidgetDock
      contextRail={contextRail}
      currentSnapshotId={currentSnapshotId ?? null}
      examplePrompts={DEFAULT_DOCK_EXAMPLE_PROMPTS}
      isStreaming={isStreaming}
      items={dockItems}
      latestProfileRecencyToken={latestProfileRecencyToken}
      latestProfileSnapshotId={latestProfileSnapshotId}
      onCollapseWorkspace={handleToggleWorkspace}
      onExamplePrompt={handleSubmit}
      snapshot={snapshot}
      token={token}
    />
  ) : dockItems.length > 0 ? (
    <AdvisorWidgetDock
      currentSnapshotId={currentSnapshotId ?? null}
      examplePrompts={DEFAULT_DOCK_EXAMPLE_PROMPTS}
      isStreaming={isStreaming}
      items={dockItems}
      latestProfileRecencyToken={latestProfileRecencyToken}
      latestProfileSnapshotId={latestProfileSnapshotId}
      onCollapseWorkspace={handleToggleWorkspace}
      onExamplePrompt={handleSubmit}
      snapshot={snapshot}
      token={token}
    />
  ) : undefined;

  // Prepend the optimistic pending row (if any) and select it. While pending
  // is set the list is never "loading" — we have a row to show.
  const sidebarConversations = pendingConversation
    ? [pendingConversation, ...(conversations ?? [])]
    : (conversations ?? []);
  const sidebarActiveConversationId = pendingConversation
    ? pendingConversation.id
    : conversationId;
  const sidebarIsLoading = conversations === null && !pendingConversation;

  return (
    <div
      className={cn(
        "grid w-full items-stretch motion-safe:transition-[grid-template-columns] motion-safe:duration-[420ms] motion-safe:ease-[cubic-bezier(0.2,0,0,1)] motion-reduce:transition-none xl:h-full xl:min-h-0",
        hydrated && token
          ? isSidebarOpen
            ? "grid-cols-1 xl:grid-cols-[280px_minmax(0,1fr)]"
            : "grid-cols-1 xl:grid-cols-[44px_minmax(0,1fr)]"
          : "grid-cols-1",
      )}
      data-slot="advisor-live-chat"
    >
      {hydrated && token ? (
        <>
          {/* Mobile backdrop — tap to close */}
          {isMobileSidebarOpen && (
            <div
              aria-hidden
              className="fixed inset-0 z-40 bg-black/20 xl:hidden"
              onClick={handleToggleMobileSidebar}
            />
          )}

          {isMobileSidebarOpen ? (
            <div className="fixed inset-y-0 left-0 z-50 flex transition-transform duration-200 ease-in-out xl:hidden">
              <AdvisorConversationSidebar
                activeConversationId={sidebarActiveConversationId}
                conversations={sidebarConversations}
                isCollapsed={false}
                isLoading={sidebarIsLoading}
                isStreaming={isStreaming}
                onNewChat={handleMobileNewChat}
                onResume={handleResume}
                onToggle={handleToggleMobileSidebar}
              />
            </div>
          ) : (
            <HistoryEdgeToggle
              className="fixed left-4 top-16 z-40 xl:hidden"
              onClick={handleToggleMobileSidebar}
            />
          )}

          <div
            className="hidden min-w-0 overflow-hidden xl:block xl:h-full xl:self-stretch"
            data-slot="advisor-history-door"
          >
            <AdvisorConversationSidebar
              activeConversationId={sidebarActiveConversationId}
              conversations={sidebarConversations}
              isCollapsed={!isSidebarOpen}
              isLoading={sidebarIsLoading}
              isStreaming={isStreaming}
              onNewChat={handleNewChat}
              onResume={handleResume}
              onToggle={handleToggleSidebar}
            />
          </div>
        </>
      ) : null}

      <div className="relative min-w-0 xl:h-full xl:min-h-0">
        <AdvisorChatSurface
          contextRail={dock}
          currentProfileRecencyToken={latestProfileRecencyToken}
          initialDraft={initialDraft}
          isHydrating={isHydrating}
          isStreaming={isStreaming}
          isWorkspaceCollapsed={isWorkspaceCollapsed}
          key={messages.length === 0 ? (initialDraft ?? "") : "active"}
          messages={messages}
          onFollowup={handleSubmit}
          onSubmitPrompt={handleSubmit}
          onStopStreaming={handleStopStreaming}
          onToggleWorkspace={handleToggleWorkspace}
          suggestions={suggestions}
        />
      </div>
    </div>
  );
}

function HistoryEdgeToggle({
  className,
  onClick,
}: {
  className?: string;
  onClick: () => void;
}) {
  return (
    <button
      aria-label="Open chat history"
      className={cn(
        "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border bg-background text-muted-foreground shadow-lg transition-[background-color,color,transform,opacity,box-shadow] hover:bg-card hover:text-foreground active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 xl:bg-background/95 xl:backdrop-blur",
        className,
      )}
      data-slot="advisor-history-edge-toggle"
      onClick={onClick}
      title="Open chat history"
      type="button"
    >
      <PanelLeft aria-hidden className="h-4 w-4" />
    </button>
  );
}
